import { create } from 'zustand';
import { gl } from './gl.js';
import { es } from './es.js';
import { en } from './en.js';

/**
 * i18n PROPIO del editor (contrato §6).
 *
 * No reutiliza el de besbello-web: son dos aplicaciones distintas. Lo unico que
 * debe coincidir es el JUEGO DE IDIOMAS (gl/es/en) y respetar el `locale` que
 * llega por SSO.
 *
 * Los diagnosticos y los conceptos de precio NO se traducen aqui por su texto:
 * llegan con un CODIGO estable desde `rules-core`, y aqui se busca ese codigo.
 * Asi el servidor puede rechazar en un idioma y la pantalla explicarlo en otro
 * sin que nada se desincronice.
 */
export type Locale = 'gl' | 'es' | 'en';
export const LOCALES: Locale[] = ['gl', 'es', 'en'];
export const LOCALE_NAMES: Record<Locale, string> = { gl: 'Galego', es: 'Castellano', en: 'English' };

/**
 * El galego es el diccionario de REFERENCIA: su forma define las claves que
 * deben existir. Los otros dos se comprueban contra el con `satisfies`, asi que
 * olvidar una clave al traducir rompe la compilacion, no la pantalla.
 */
export type Dictionary = typeof gl;

const DICTIONARIES: Record<Locale, Dictionary> = {
  gl,
  es: es satisfies Dictionary,
  en: en satisfies Dictionary,
};

interface I18nState {
  locale: Locale;
  setLocale(locale: Locale): void;
}

function detectInitialLocale(): Locale {
  // Prioridad: `?locale=` (override explicito del contrato) > preferencia
  // guardada > idioma del navegador > galego.
  if (typeof window !== 'undefined') {
    const fromQuery = new URLSearchParams(window.location.search).get('locale');
    if (fromQuery && LOCALES.includes(fromQuery as Locale)) return fromQuery as Locale;

    const stored = window.localStorage?.getItem('bb_locale');
    if (stored && LOCALES.includes(stored as Locale)) return stored as Locale;

    const browser = window.navigator?.language?.slice(0, 2);
    if (browser && LOCALES.includes(browser as Locale)) return browser as Locale;
  }
  return 'gl';
}

export const useI18n = create<I18nState>((set) => ({
  locale: detectInitialLocale(),
  setLocale: (locale) => {
    if (typeof window !== 'undefined') {
      window.localStorage?.setItem('bb_locale', locale);
      document.documentElement.lang = locale;
    }
    set({ locale });
  },
}));

/** Busca `a.b.c` en el diccionario, con respaldo al galego si falta. */
function lookup(dictionary: Dictionary, key: string): string | undefined {
  return key.split('.').reduce<unknown>((acc, part) => {
    if (acc && typeof acc === 'object' && part in acc) return (acc as Record<string, unknown>)[part];
    return undefined;
  }, dictionary) as string | undefined;
}

export function translate(locale: Locale, key: string, params?: Record<string, string | number | boolean>): string {
  const text = lookup(DICTIONARIES[locale], key) ?? lookup(DICTIONARIES.gl, key);
  // Devolver la clave (y no una cadena vacia) hace que una traduccion olvidada
  // se vea en pantalla en vez de dejar un hueco silencioso.
  if (text === undefined) return key;
  if (!params) return text;
  return text.replace(/\{(\w+)\}/g, (match, name: string) =>
    params[name] === undefined ? match : String(params[name]),
  );
}

export function useT(): (key: string, params?: Record<string, string | number | boolean>) => string {
  const locale = useI18n((s) => s.locale);
  return (key, params) => translate(locale, key, params);
}

/** Formatea centimos en el idioma activo. */
export function useMoney(): (cents: number) => string {
  const locale = useI18n((s) => s.locale);
  const tag = locale === 'en' ? 'en-IE' : locale === 'es' ? 'es-ES' : 'gl-ES';
  return (cents: number) =>
    new Intl.NumberFormat(tag, { style: 'currency', currency: 'EUR' }).format(cents / 100);
}

export function useDate(): (iso: string) => string {
  const locale = useI18n((s) => s.locale);
  const tag = locale === 'en' ? 'en-IE' : locale === 'es' ? 'es-ES' : 'gl-ES';
  return (iso: string) => new Intl.DateTimeFormat(tag, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(iso));
}
